import {
  DeclineProposalRequest,
  type GetRequest,
  type NewUpdateWithTokenRequest,
  type Proposal,
  ProposalPdfRequest,
  type SendProposalReminderRequest,
  TokenProposal,
  type TokenProposalData,
  type UpdateRequest,
  type UpdateWithTokenRequest,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { ProposalClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type ProposalFilter = EntityFilter<Proposal>;

export const useProposalGetQuery = ({
  enabled = true,
  filter,
}: {
  enabled?: boolean;
  filter: GetRequest;
}) =>
  useQuery({
    queryKey: [queryKeys.proposal.root, queryKeys.proposal.getHash(filter)],
    queryFn() {
      return ProposalClientService.Get(filter);
    },
    enabled,
  });

export const useProposalGetInlineQuery = () => {
  const queryClient = useQueryClient();

  const fetch = useCallback(
    async (filter: GetRequest) => {
      const queryKey = [queryKeys.proposal.root, queryKeys.proposal.getHash(filter)];
      const cache = queryClient.getQueryData<Proposal>(queryKey);
      if (!cache) {
        const res = await ProposalClientService.Get(filter);
        queryClient.setQueryData(queryKey, res);
        return res;
      }
      return cache;
    },
    [queryClient],
  );

  return useMemo(() => ({ fetch }), [fetch]);
};

export const useProposalUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: UpdateRequest) => ProposalClientService.Update(req),
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.proposal.root],
      });
    },
  });
};

export const useProposalCreateTokenMutation = () => {
  return useMutation({
    mutationFn: (req: TokenProposalData) => ProposalClientService.CreateToken(req),
  });
};

export const useProposalGetWithTokenQuery = ({
  enabled = true,
  filter,
}: {
  enabled?: boolean;
  filter: TokenProposal;
}) =>
  useQuery({
    queryKey: [queryKeys.proposal.root, 'token', queryKeys.proposal.getHash(filter)],
    queryFn() {
      return ProposalClientService.GetWithToken(filter);
    },
    enabled,
  });

export const useProposalUpdateWithTokenMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: UpdateWithTokenRequest) => ProposalClientService.UpdateWithToken(req),
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.proposal.root],
      });
    },
  });
};

/** Token-based update (NewUpdateWithToken) for customer signing - includes quoteDocumentId. */
export const useProposalNewUpdateWithTokenMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: NewUpdateWithTokenRequest) => ProposalClientService.newUpdateWithToken(req),
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.proposal.root],
      });
    },
  });
};

export const useProposalPreviewQuery = ({
  proposalId,
  quoteDocumentId,
  enabled = true,
}: {
  proposalId: number;
  quoteDocumentId?: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.proposal.root, 'preview', proposalId, quoteDocumentId],
    queryFn: async () => {
      const request = ProposalPdfRequest.create();
      request.jobId = proposalId;
      if (quoteDocumentId != null) {
        request.quoteDocumentId = quoteDocumentId;
      }

      const response = await ProposalClientService.getProposalPreview(request);

      if (!response || !response.url) {
        throw new Error('Failed to generate proposal preview URL');
      }

      return response.url;
    },
    enabled,
  });
};

export const useSendProposalReminderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: SendProposalReminderRequest) =>
      ProposalClientService.SendProposalReminder(req),
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteDocument.root],
      });
    },
  });
};

/** Dry-run of the automated reminder decision — reports what the hourly sweep would
 * do and why, without sending. A mutation because it is fetched on demand and the
 * answer changes with the clock, so caching it would lie. */
export const useEvaluateProposalReminderMutation = () =>
  useMutation({
    mutationFn: (req: SendProposalReminderRequest) =>
      ProposalClientService.EvaluateProposalReminder(req),
  });

export const useProposalDeclineContextQuery = ({
  token,
  enabled = true,
}: {
  token: string;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [queryKeys.proposal.root, 'declineContext', token],
    queryFn: () =>
      ProposalClientService.GetProposalDeclineContext(TokenProposal.create({ value: token })),
    enabled,
    retry: false,
  });

export const useDeclineProposalMutation = () =>
  useMutation<boolean, Error, { token: string; reason: string }>({
    mutationFn: async ({ token, reason }) => {
      const req = DeclineProposalRequest.create({
        token: TokenProposal.create({ value: token }),
        reason,
      });
      const response = await ProposalClientService.DeclineProposalWithToken(req);
      return response?.value || false;
    },
  });
